
import { supabase } from '@/integrations/supabase/client';
import { Sentry } from '@/lib/sentry';

// Define the rrweb event type based on rrweb-player expectations
export interface RrwebEvent {
  type: number;
  data: any;
  timestamp: number;
}

export interface EventWithTime extends RrwebEvent {
  timestamp: number;
}

/**
 * Fetches and parses rrweb session events from Supabase Storage.
 * @param storagePath The path to the events JSON file in the 'raw-sessions' bucket.
 * @returns A promise that resolves to an array of rrweb events or null if an error occurs.
 */
export const loadSessionEvents = async (storagePath: string): Promise<EventWithTime[] | null> => {
  try {
    console.log('Loading session events from:', storagePath);
    
    const { data, error } = await supabase.storage
      .from('raw-sessions')
      .download(storagePath);

    if (error) {
      console.error('Error downloading session events:', error);
      Sentry.captureException(error);
      throw error;
    }

    if (!data) {
      console.error('No data received for session events.');
      const noDataError = new Error('No data received for session events.');
      Sentry.captureException(noDataError);
      throw noDataError;
    }

    const text = await data.text();
    console.log('Downloaded events data size:', text.length, 'characters');
    
    let parsedData;
    try {
      parsedData = JSON.parse(text);
    } catch (parseError) {
      console.error('Failed to parse JSON:', parseError);
      Sentry.captureException(parseError);
      throw new Error('Invalid JSON format in events file');
    }

    // Handle different possible storage formats
    let events: EventWithTime[];
    
    if (Array.isArray(parsedData)) {
      // Direct array of events
      events = parsedData;
    } else if (parsedData && typeof parsedData === 'object' && Array.isArray(parsedData.events)) {
      // Object with events array (common rrweb storage pattern)
      events = parsedData.events;
    } else if (parsedData && typeof parsedData === 'object') {
      // Try to find any array property that might contain events
      const potentialEventsKey = Object.keys(parsedData).find(key => 
        Array.isArray(parsedData[key]) && parsedData[key].length > 0
      );
      
      if (potentialEventsKey) {
        events = parsedData[potentialEventsKey];
      } else {
        throw new Error('No valid events array found in data structure');
      }
    } else {
      throw new Error('Events data is not in a recognized format');
    }

    // Validate that we have events and they look like rrweb events
    if (!events || events.length === 0) {
      console.warn('No events found in the loaded data');
      return [];
    }

    // Basic validation of event structure
    const validEvents = events.filter(event => 
      event && 
      typeof event === 'object' && 
      typeof event.type === 'number' && 
      typeof event.timestamp === 'number'
    );

    if (validEvents.length === 0) {
      throw new Error('No valid rrweb events found in the data');
    }

    if (validEvents.length !== events.length) {
      console.warn(`Filtered out ${events.length - validEvents.length} invalid events`);
    }

    console.log(`Successfully loaded ${validEvents.length} rrweb events`);
    return validEvents;

  } catch (err) {
    console.error('Failed to load or parse session events:', err);
    Sentry.captureException(err);
    return null;
  }
};

/**
 * Validates if a given object looks like a valid rrweb event
 */
export const isValidRrwebEvent = (event: any): event is EventWithTime => {
  return (
    event &&
    typeof event === 'object' &&
    typeof event.type === 'number' &&
    typeof event.timestamp === 'number' &&
    event.data !== undefined
  );
};

/**
 * Gets the duration of a session from its events
 */
export const getSessionDuration = (events: EventWithTime[]): number => {
  if (!events || events.length === 0) return 0;
  
  const firstEvent = events[0];
  const lastEvent = events[events.length - 1];
  
  return lastEvent.timestamp - firstEvent.timestamp;
};

/**
 * Gets the initial snapshot from events (usually the first event with type 2)
 */
export const getInitialSnapshot = (events: EventWithTime[]) => {
  return events.find(event => event.type === 2); // Full snapshot event type
};
